import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Filter, FilterGroup } from './types';

type RequestOptions = {
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Minimal http client the widgets need. Structurally compatible with the
 * admin app's `api` object (cookie auth); the host injects it.
 */
export type WidgetsHttpClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
};

export type WidgetsContextValue = {
  projectId: string;
  filterGroups: FilterGroup[];
  api: WidgetsHttpClient;
  /** Optional — when omitted, widgets hide their "Filter" affordances. */
  addFilter?: (filter: Filter) => void;
  isActiveFilter?: (filter: Filter) => boolean;
};

const WidgetsContext = createContext<WidgetsContextValue | null>(null);

export const useWidgetsContext = (): WidgetsContextValue => {
  const value = useContext(WidgetsContext);
  if (!value) {
    throw new Error('useWidgetsContext must be used within a WidgetsProvider');
  }
  return value;
};

export const WidgetsProvider = ({
  projectId,
  filterGroups,
  api,
  addFilter,
  isActiveFilter,
  children,
}: WidgetsContextValue & { children: ReactNode }) => {
  const value = useMemo(
    () => ({ projectId, filterGroups, api, addFilter, isActiveFilter }),
    [projectId, filterGroups, api, addFilter, isActiveFilter],
  );

  return (
    <WidgetsContext.Provider value={value}>{children}</WidgetsContext.Provider>
  );
};
